import { useEffect, useRef, useState } from "react";
import { useSocket } from "../components/socket/SocketProvider";
import { appointmentClose } from "../service/videoCallService";
import { resolveCallExitPath } from "./afterCallNavigate";

const HIDDEN_CALL_TIMEOUT = 60 * 60 * 1000; // 1 hour hidden tab during call
const INACTIVITY_TIMEOUT = 60 * 60 * 1000;

function usePageVisibility(token, socket, userId) {
  const offlineTimeoutRef = useRef(null);
  const inactivityTimeoutRef = useRef(null);
  const [isOnline, setIsOnline] = useState(false);

  useSocket("userStatusResponse", (data) => {
    if (userId === data?.userId) {
      setIsOnline(data.online);
    }
  });

  useEffect(() => {
    const getAppointment = () => JSON.parse(localStorage.getItem("appointmentData") || "null");
    const getUserDetails = () => JSON.parse(localStorage.getItem("userDetails") || "null");

    // Soft no-op: presence is determined by active sockets + heartbeats on the server.
    // Do not trust beforeunload/pagehide as offline authority.
    const emitUserOffline = () => {};

    const handleAppointmentClose = () => {
      const appointment = getAppointment();
      const userDetails = getUserDetails();
      if (!appointment?.appointmentId) return;

      // Long hidden tab after real session — bill used time
      appointmentClose({
        appointmentId: appointment.appointmentId,
        interrupted: false,
        mediaEstablished: true,
      }).then(() => {
        const eventName = appointment.mode === "chat" ? "chatDisconnect" : "videoSessionDisconnect";

        socket?.emit(eventName, {
          disconnect: true,
          appointmentId: appointment.appointmentId,
        });

        socket?.emit("consultantBusy", {
          consultantId: appointment.consultantId,
          userId: appointment.callerData?.user?.id,
          isBusy: false,
        });

        localStorage.removeItem("joined");
        localStorage.removeItem("appointmentData");

        const redirectPath = resolveCallExitPath({
          appointmentId: appointment.appointmentId,
          roleCode: userDetails?.roleCode,
          myId: userDetails?._id,
          appointment,
        });

        window.location.href = redirectPath;
      });
    };

    const handleVisibilityChange = () => {
      const appointment = getAppointment();
      if (document.hidden && appointment?.appointmentId) {
        offlineTimeoutRef.current = setTimeout(handleAppointmentClose, HIDDEN_CALL_TIMEOUT);
      } else {
        clearTimeout(offlineTimeoutRef.current);
        offlineTimeoutRef.current = null;
      }
    };

    const resetInactivityTimeout = () => {
      clearTimeout(inactivityTimeoutRef.current);
      inactivityTimeoutRef.current = setTimeout(emitUserOffline, INACTIVITY_TIMEOUT);
    };

    const eventListeners = [
      { event: "visibilitychange", handler: handleVisibilityChange },
      { event: "mousemove", handler: resetInactivityTimeout },
      { event: "keydown", handler: resetInactivityTimeout },
    ];

    eventListeners.forEach(({ event, handler }) => {
      document.addEventListener(event, handler);
    });

    resetInactivityTimeout();

    return () => {
      eventListeners.forEach(({ event, handler }) => {
        document.removeEventListener(event, handler);
      });
      [offlineTimeoutRef, inactivityTimeoutRef].forEach((ref) => {
        if (ref.current) clearTimeout(ref.current);
      });
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token, socket, userId]);

  return isOnline;
}

export default usePageVisibility;
